<template>
  <ValidationObserver ref="observer" tag="form" v-slot="{ passes }" @submit.prevent="onSubmitForm">
    <el-dialog title="Product Attribute" :visible.sync="syncModal" :close-on-click-modal="false"
      append-to-body width="600px" @closed="resetForm">
      <mdb-container class="mt-0 mb-0">
        <ThatsInput label="Name" vid="name" name="name" v-model="form.name" :validated="validated"
          rules="required" size="sm" />
        <ThatsMultiSelect
          labelClass="col-sm-3"
          fieldClass="col-sm-9"
          label="Data Type"
          vid="data_type"
          name="data_type"
          v-model="form.data_type"
          :options="activeDataTypes"
          :multiple="false"
          :reduce="type => type.value"
          :reduceLabel="'text'"
          :validated="validated"
          :rules="'required'"
          size="sm"
        />
        <ThatsInput v-if="supportsUnitType" label="Unit Type" vid="unit_type" name="unit_type"
          v-model="form.unit_type" size="sm" />
        <ThatsAttribute v-if="form.data_type === 'LIST'" label="Options" labelClass="col-sm-3"
          fieldClass="col-sm-9" v-model="optionItems" @input="optionsChanged" />
        <ThatsRadio label="Status" vid="active" name="active" orientation="inline" :data="statusOptions"
          v-model="form.active" :validated="validated" rules="required" size="sm" />
      </mdb-container>
      <template slot="footer">
        <mdb-btn v-if="formStatus === 'update'" color="elegant" size="sm" @click.native="openHistoryDialog">
          History
        </mdb-btn>
        <mdb-btn color="elegant" size="sm" :disabled="formLoading" @click.native="passes(onSubmitForm)">
          <span v-if="formLoading" class="spinner-border spinner-border-sm mr-1"></span>
          {{ formLoading ? 'Loading...' : 'Save' }}
        </mdb-btn>
        <mdb-btn color="grey" size="sm" @click.native="closeDialog">Close</mdb-btn>
      </template>
    </el-dialog>
    <HistoryDialog ref="historyDialog" />
  </ValidationObserver>
</template>

<script>
import { mdbBtn, mdbContainer } from 'mdbvue'
import { ValidationObserver } from 'vee-validate'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { ThatsInput, ThatsRadio, ThatsMultiSelect, ThatsAttribute } from '../../components/thats/Thats'
import { ApiHttpService } from '../../services'
import HistoryDialog from '../../components/thats/HistoryDialog.vue'

export default {
  components: { ValidationObserver, ThatsInput, ThatsRadio, ThatsMultiSelect, ThatsAttribute, mdbBtn, mdbContainer, HistoryDialog },
  data() {
    return {
      syncModal: false,
      validated: false,
      formLoading: false,
      form: this.formVar(),
      dataTypes: [
        { text: 'TEXT', value: 'TEXT' , active: false },
        { text: 'NUMBER', value: 'NUMBER' , active: false },
        { text: 'Yes/No', value: 'BOOLEAN' , active: true },
        { text: 'LIST', value: 'LIST' , active: true },
      ],
      statusOptions: [{ label: 'Active', value: 'true' }, { label: 'Inactive', value: 'false' }],
      options: [],
      optionItems: [],
    }
  },
  computed: {
    ...mapGetters('ProductAttributeModule', ['formStatus', 'formRecord']),
    supportsUnitType() {
      return ['TEXT', 'NUMBER'].includes(this.form.data_type)
    },

    activeDataTypes() {
      return this.dataTypes.filter(type => type.active)
    }
  },
  methods: {
    ...mapActions('ProductAttributeModule', ['storeRecord', 'updateRecord']),
    ...mapMutations('ProductAttributeModule', ['setFormStatusCreate']),
    ...mapMutations('HistoryModule', ['setModule', 'setRecordId']),
    formVar() {
      return { name: '', data_type: 'TEXT', unit_type: '', active: 'true' }
    },
    dataTypeChanged(value) {
      if (!['TEXT', 'NUMBER'].includes(value)) {
        this.form.unit_type = ''
      }
      if (value !== 'LIST') {
        this.options = []
        this.optionItems = []
      }
    },
    openDialog() {
      this.syncModal = true
    },
    closeDialog() {
      this.syncModal = false
    },
    openHistoryDialog() {
      if (!this.form || !this.form.id) return

      this.setModule('product-attribute')
      this.setRecordId(this.form.id)
      this.$refs.historyDialog.openDialog()
    },
    optionsChanged(items) {
      const previousOptions = this.options
      const nextOptions = items
        .map((item, index) => {
          const existing = previousOptions.find(option => option.id && String(option.id) === String(item.id))
          return existing
            ? { ...existing, value: item.text, sort_order: index }
            : { value: item.text, sort_order: index, active: true }
        })
        .filter(option => option.value && option.value.trim())

      const removedOptions = previousOptions.filter(option =>
        option.id && !nextOptions.some(item => String(item.id) === String(option.id))
      )

      this.options = nextOptions
      this.optionItems = items
      removedOptions.forEach(option => {
        ApiHttpService.deleteRequest('product-attributes/options', { ids: [option.id] })
      })
    },
    syncOptionItems() {
      this.optionItems = this.options.map((option, index) => ({
        id: option.id,
        index: option.id || `option-${index}`,
        text: option.value,
        slug: option.value,
      }))
    },
    async fetchOptions(attributeId) {
      const response = await ApiHttpService.getRequest('product-attributes/options', { product_attribute_id: attributeId })
      this.options = response.data.data || response.data || []
      this.syncOptionItems()
    },
    async saveOptions(attributeId) {
      const requests = this.options.map((option, index) => {
        const payload = {
          product_attribute_id: attributeId,
          value: option.value,
          sort_order: index,
          active: option.active === undefined ? true : option.active,
        }
        return option.id
          ? ApiHttpService.update('product-attributes/options', { ...payload, id: option.id })
          : ApiHttpService.postRequest('product-attributes/options', payload)
      })

      if (requests.length) {
        await Promise.all(requests)
        await this.fetchOptions(attributeId)
      }
    },
    onSubmitForm() {
      if (this.formLoading) return
      this.validated = true
      this.formLoading = true
      const payload = { ...this.form, active: [true, 1, '1', 'true'].includes(this.form.active) }
      const request = this.formStatus === 'create' ? this.storeRecord(payload) : this.updateRecord({ ...payload, id: this.form.id })
      request.then(response => {
        const attribute = response.data || response
        if (attribute && attribute.id && this.form.data_type === 'LIST') {
          return this.saveOptions(attribute.id).then(() => response)
        }
        return response
      }).then(response => {
        this.$emit('success', response)
        this.closeDialog()
      }).catch(error => {
        if (error.response && error.response.data && error.response.data.errors) {
          this.$refs.observer.setErrors(error.response.data.errors)
        }
      }).finally(() => { this.formLoading = false })
    },
    resetForm() {
      this.form = this.formVar()
      this.options = []
      this.optionItems = []
      this.validated = false
      this.formLoading = false
      this.setFormStatusCreate()
      this.$nextTick(() => this.$refs.observer && this.$refs.observer.reset())
    },
  },
  watch: {
    'form.data_type': 'dataTypeChanged',
    formRecord(value) {
      if (this.formStatus === 'update' && value) {
        this.form = { ...this.formVar(), ...(value.row || value) }
        this.form.active = [true, 1, '1', 'true'].includes(this.form.active) ? 'true' : 'false'
        if (this.form.data_type === 'LIST' && this.form.id) {
          this.fetchOptions(this.form.id)
        } else {
          this.options = []
          this.optionItems = []
        }
      }
    },
  },
}
</script>
